import React, { useState, useEffect } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { colors, carbon, Segment } from '@waylay/react-components'
import {
  get,
  omit,
  startsWith,
  reduce,
  toString,
  isObject,
  isNumber,
} from 'lodash-es'
import clientContainer from '~/lib/client'
import { CONFIGURATIONS_LAYOUT, PROPERTIES_LAYOUT } from '~/lib/Constants'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Icon } from '@waylay/react-components'
import { useToasts } from 'react-toast-notifications'
import { AnnotationType } from '@waylay/bn-parser'
import { TaskStatus } from '~/lib/types'

const formatContent = content =>
  isObject(content) ? JSON.stringify(content) : toString(content)

const Property = ({ name, value: propValue }) => {
  const { value, resolved } = propValue
  const niceName = PROPERTIES_LAYOUT[name] || name
  const text = parsePropertyValue(value)

  return <InformationItem label={niceName} value={text} resolved={resolved} />
}

const Configuration = ({ name, value: propValue }) => {
  const { value, resolved } = propValue
  const niceName = CONFIGURATIONS_LAYOUT[name] || name
  const text = parsePropertyValue(value)

  return <InformationItem label={niceName} value={text} resolved={resolved} />
}

const InformationItem = ({ label, value, resolved }) => (
  <>
    <strong>{label}</strong>
    <span css={textOverflow} title={formatContent(value)}>
      {formatContent(value)}
    </span>
    {resolved !== undefined && (
      <>
        <span />
        <span css={textOverflow} title={formatContent(resolved)}>
          ↳ {formatContent(resolved)}
        </span>
      </>
    )}
  </>
)

const parsePropertyValue = value => {
  if (value === true) return 'true'
  else if (value === false) return 'false'
  else if (isObject(value)) return JSON.stringify(value)
  else if (isNumber(value)) return value
  else return toString(value) || '-'
}

export const ResumeNodeExecutionButton = ({
  node,
  taskId,
  pendingNodes,
  toggleNodeAnnotation,
  taskStatus,
}: {
  node: any
  taskId?: string
  pendingNodes?: any
  toggleNodeAnnotation?: any
  taskStatus?: string
}) => {
  const { addToast } = useToasts()
  const [isNodePending, setIsNodePending] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  const resumeMutation = useMutation({
    mutationFn: async () => {
      return await clientContainer.client.tasks.performNodeAction(
        taskId,
        node.label,
        {
          operation: 'resume',
        },
      )
    },
    onSuccess: () => {
      addToast('Successfully resumed node execution', {
        appearance: 'success',
      })
      setIsNodePending(false)
    },
    onError: () => {
      addToast('Failed to resume node execution', { appearance: 'error' })
    },
    throwOnError: false,
  })

  useEffect(() => {
    if (pendingNodes && node?.label) {
      const pendingNodeNames = Object.keys(pendingNodes)
      const isPendingNode = pendingNodeNames.includes(node.label)
      toggleNodeAnnotation?.(
        isPendingNode,
        node.id,
        AnnotationType.pendingNodes,
      )
      setIsNodePending(pendingNodeNames.includes(node.label))
    }
  }, [node, pendingNodes])

  useEffect(() => {
    setIsButtonDisabled(taskStatus === TaskStatus.Stopped || !taskId)
  }, [taskStatus, taskId])

  if (!isNodePending) {
    return null
  }

  return (
    <Button
      onClick={() => resumeMutation.mutate()}
      disabled={isButtonDisabled}
      style={{ width: '100%' }}
    >
      <Icon name="pending" style={{ marginRight: '5px' }} />
      {resumeMutation.status === 'pending'
        ? 'Resuming...'
        : 'Resume Node Execution'}
    </Button>
  )
}
const NodeInformation = ({
  node,
  resource: resourceId,
  variables,
  taskId,
  pendingNodes,
  taskStatus,
}: {
  node: any
  resource?: string
  variables?: any
  taskId?: string
  pendingNodes?: any
  taskStatus?: string
}) => {
  if (!node.configuration) {
    // gates and transition blocks don't have a configuration
    return null
  }

  // Add dataTrigger and tickTrigger to regular properties
  const {
    dataTrigger,
    tickTrigger,
    pauseExecutionTimeout,
    pauseExecution,
    ...configuration
  } = omit(get(node, 'configuration', {}), 'properties')

  const properties = {
    ...get(node, 'configuration.properties', {}),
    dataTrigger,
    tickTrigger,
    pauseExecution,
    pauseExecutionTimeout,
  }

  // Enhance properties with resolved meta values
  const fetchResourceFn = id => {
    if (!id) {
      return Promise.resolve({})
    }

    return clientContainer.client.resources.get(id)
  }

  const { isLoading, data: resource } = useQuery({
    queryKey: ['configuration-key', resourceId],
    queryFn: () => fetchResourceFn(resourceId),
    gcTime: 0,
  })

  const [enhancedConfiguration, setEnhancedConfiguration] = useState({})
  const [enhancedProperties, setEnhancedProperties] = useState({})

  useEffect(() => {
    const enhancedProps = reduce(
      configuration,
      (result, value, key) => {
        result[key] = { value }

        if (startsWith(value, 'META.$.')) {
          const variableName = value.replace('META.$.', '')
          const resourceRef = decodeURI(
            get(resource, [variableName, '$ref'], ''),
          ).replace('/resources/', '')
          if (resourceRef !== '') result[key] = { value, resolved: resourceRef }
        }

        if (startsWith(value, '$')) {
          const variableName = value.replace('$', '')
          const resourceRef = get(variables, [variableName], '')
          if (resourceRef !== '') result[key] = { value, resolved: resourceRef }
        }

        return result
      },
      {},
    )

    setEnhancedConfiguration(enhancedProps)
  }, [node, resource])

  useEffect(() => {
    const enhancedProps = reduce(
      properties,
      (acc, value, key) => {
        acc[key] = { value }

        if (startsWith(value, '$')) {
          const variableName = value
            .replace(/\${variables./g, '')
            .replace(/\${task./g, '')
            .replace(/}/g, '')
          const resourceRef =
            variableName.toLowerCase() === 'resource'
              ? resourceId
              : get(variables, [variableName], '')
          if (resourceRef !== '') acc[key] = { value, resolved: resourceRef }
        }

        return acc
      },
      {},
    )

    setEnhancedProperties(enhancedProps)
  }, [node])

  if (isLoading) return null

  return (
    <Segment.Group>
      <ResumeNodeExecutionButton
        node={node}
        taskId={taskId}
        pendingNodes={pendingNodes}
        taskStatus={taskStatus}
      />
      <Segment.Header>Node Configuration</Segment.Header>
      <GridSegment>
        {Object.keys(enhancedProperties).length === 0 ? (
          <EmptyProperties>No properties</EmptyProperties>
        ) : (
          Object.entries(enhancedProperties)
            .filter(([key]) => {
              // Hide pauseExecutionTimeout if pauseExecution is not true
              if (key === 'pauseExecutionTimeout') {
                const pauseExecution =
                  enhancedProperties['pauseExecution']?.value
                return pauseExecution === true
              }
              return true
            })
            .map(([key, value]) => (
              <Property key={key} name={key} value={value} />
            ))
        )}
      </GridSegment>
      <Segment.Header
        css={css`
          border-top: none;
        `}
      >
        Advanced configuration
      </Segment.Header>
      <GridSegment>
        {Object.entries(enhancedConfiguration).map(([key, value]) => (
          <Configuration key={key} name={key} value={value} />
        ))}
      </GridSegment>
    </Segment.Group>
  )
}

export const GridSegment = styled(Segment)`
  display: grid;
  grid-column-gap: 1rem;
  grid-template-columns: max-content auto;
`

const EmptyProperties = styled.div`
  color: ${colors.withWeight(carbon, 300)};
  text-align: center;
  line-height: 0;
  padding: 0.5rem 0;
`

const textOverflow = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default NodeInformation
